import { type ReactNode } from 'react';

import { Box, type BoxProps } from '@mui/material';

/**
 * A block centred in the screen, rather than in what is left of the screen.
 *
 * A `flex: 1` box centres its content between whatever sits above it and
 * whatever sits below, which is the middle of the frame only when those two are
 * the same height. A status bar and a home indicator are, so most screens are
 * already centred and pass nothing here. A bar is not: it is 60px at the top
 * with nothing answering it at the bottom, so a wait or an empty state under
 * one lands 30px low and reads as sagging.
 *
 * `offset` is the fix and the whole of it: the difference in height between the
 * chrome above the box and the chrome below, in px. Bottom padding of that
 * amount moves the content up by half of it, which is exactly the gap, so a
 * screen with a bar above and a home indicator below passes the bar's height.
 *
 * Padding rather than a negative margin or a transform: the box stays the size
 * it was, so anything inside it that scrolls scrolls against the same bounds,
 * and nothing can be dragged out under the bar.
 */
const ScreenCenter = ({
  offset = 0,
  px = 3,
  children,
  sx,
}: {
  /** How much taller the chrome above is than the chrome below, in px. */
  offset?: number;
  /** Side gutters, on the spacing scale. The screen default of 24px. */
  px?: number;
  children?: ReactNode;
  sx?: BoxProps['sx'];
}) => (
  <Box
    sx={[
      {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px,
        // px, because the chrome heights it answers are px constants rather
        // than steps on the spacing scale.
        pb: `${offset}px`,
      },
      ...(Array.isArray(sx) ? sx : [sx ?? {}]),
    ]}
  >
    {children}
  </Box>
);

ScreenCenter.displayName = 'ScreenCenter';
export default ScreenCenter;
